import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import Skeleton from '../components/Skeleton';
import { RowView, resolveRows } from './HomePage';
import { STORAGE_BASE } from '../config';
import { DefaultTemplateList } from '../sections/ListTemplate';
import Breadcrumb from '../components/Breadcrumb';

const coverSrc = p => (!p ? '' : (/^https?:\/\//.test(p) ? p : `${STORAGE_BASE}/${String(p).replace(/^\/+/, '')}`));

export default function BlogPage() {
  const [searchParams] = useSearchParams();
  const [pageRows, setPageRows] = useState(null);
  const [posts, setPosts] = useState([]);
  const [cats, setCats] = useState([]);
  const [siteName, setSiteName] = useState('');
  const [activeCat, setActiveCat] = useState(searchParams.get('category') || '');
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  // موضوعات داغ = نام دسته‌های واقعی همین فروشگاه (نه لیست ثابت)
  const topics = cats.map(c => c.name).filter(Boolean).slice(0, 10);

  useEffect(() => {
    const initCat = searchParams.get('category') || '';
    setActiveCat(initCat);
    Promise.all([
      axios.get('/site-settings').catch(() => ({ data: {} })),
      axios.get('/posts', { params: { per_page: 12, category: initCat || undefined } }).catch(() => ({ data: { data: [] } })),
      axios.get('/post-categories').catch(() => ({ data: [] })),
    ]).then(async ([s, p, c]) => {
      setPosts(p.data?.data || []);
      setTotal(p.data?.total || 0);
      setCats(Array.isArray(c.data) ? c.data : []);
      setSiteName(s.data?.site_name || s.data?.siteName || '');
      const slug = s.data?.blog?.homepage;
      if (slug) {
        try {
          const pg = await axios.get(`/pages/${slug}`);
          const vw = typeof window !== 'undefined' && window.innerWidth < 768 ? 'mobile' : (typeof window !== 'undefined' && window.innerWidth < 1024 ? 'tablet' : 'desktop');
          setPageRows(resolveRows(pg.data.platforms, vw).filter(x => x.enabled !== false));
        } catch { setPageRows([]); }
      } else setPageRows([]);
      setLoading(false);
    });
  }, []);

  const search = async (cat, qOverride) => {
    setLoading(true);
    const slug = cat !== undefined ? cat : activeCat;
    const q = qOverride !== undefined ? qOverride : query;
    const r = await axios.get('/posts', { params: { per_page: 12, q: q, category: slug || undefined } }).catch(() => null);
    if (r) { setPosts(r.data?.data || []); setTotal(r.data?.total || 0); }
    setLoading(false);
  };

  if (loading && !posts.length && pageRows === null) return (
    <div style={{ width: '100%', padding: '24px clamp(10px, 3vw, 44px) 60px', direction: 'rtl' }}>
      <Skeleton height={28} width="35%" style={{ marginBottom: 16 }} />
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <Skeleton height={44} style={{ flex: 1, borderRadius: 12 }} />
        <Skeleton height={44} width={110} style={{ borderRadius: 12 }} />
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
        {[90, 110, 100, 80].map((w, i) => <Skeleton key={i} height={32} width={w} style={{ borderRadius: 999 }} />)}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
        {[0, 1, 2, 3, 4, 5].map(i => (
          <div key={i}><Skeleton height={170} style={{ borderRadius: 14 }} /><Skeleton height={18} width="80%" style={{ marginTop: 10 }} /><Skeleton height={14} width="50%" style={{ marginTop: 8 }} /></div>
        ))}
      </div>
    </div>
  );

  const isFiltered = !!activeCat || !!query;
  const hero = !isFiltered && !loading && posts.length > 0 ? posts[0] : null;
  const rest = hero ? posts.slice(1) : posts;

  return (
    <div style={{ width: '100%', padding: '24px clamp(10px, 3vw, 44px) 60px', direction: 'rtl' }}>
      <Breadcrumb items={[{ label: 'وبلاگ' }]} />
      <div style={{ background: 'linear-gradient(135deg, color-mix(in srgb, var(--primary) 12%, var(--card)), var(--card) 70%)', border: '1px solid color-mix(in srgb, var(--primary) 18%, var(--border))', borderRadius: 'var(--shape-card)', padding: 'clamp(18px, 3vw, 30px)', marginBottom: 18, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
        <div>
          <span style={{ fontSize: '0.72rem', fontWeight: 800, color: 'var(--primary)', background: 'color-mix(in srgb, var(--primary) 14%, transparent)', borderRadius: 999, padding: '5px 14px' }}>مجله {siteName || 'ما'}</span>
          <h1 className="serif" style={{ margin: '10px 0 0', fontWeight: 800, fontSize: 'clamp(1.55rem, 4.5vw, 2.3rem)', letterSpacing: '-0.01em' }}>وبلاگ و راهنماها</h1>
          <p style={{ margin: '8px 0 0', fontSize: '0.9rem', color: 'var(--muted-fg)', lineHeight: 2 }}>
            {total ? `${Number(total).toLocaleString('fa-IR')} مطلب آموزشی و خبری` : 'آخرین مطالب و راهنماها'}
            {activeCat && cats.find(c => c.slug === activeCat) ? ` · دسته ${cats.find(c => c.slug === activeCat).name}` : ''}
          </p>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        {[['مطلب منتشرشده', total || 0], ['دسته‌بندی', cats.length || 0], ['به‌روزرسانی', 'هفتگی']].map(([k, v]) => (
          <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 999, padding: '8px 16px', fontSize: '0.8rem' }}>
            <span style={{ fontWeight: 800 }}>{typeof v === 'number' ? Number(v).toLocaleString('fa-IR') : v}</span>
            <span style={{ color: 'var(--muted-fg)' }}>{k}</span>
          </div>
        ))}
      </div>
      {topics.length > 0 && (
      <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 18 }}>
        <span style={{ fontSize: '0.78rem', fontWeight: 800, color: 'var(--muted-fg)', alignSelf: 'center' }}>موضوعات داغ:</span>
        {topics.map(t => (
          <button key={t} onClick={() => { setQuery(t); search('', t); }}
            style={{ padding: '6px 13px', borderRadius: 999, border: '1px solid var(--border)', background: 'transparent', color: 'var(--fg)', fontFamily: 'inherit', fontSize: '0.76rem', fontWeight: 700, cursor: 'pointer', transition: 'all 0.18s' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.color = 'var(--primary)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--fg)'; }}>
            #{t}
          </button>
        ))}
      </div>
      )}
      <div className="blog-layout">
      <div className="blog-main">
      {pageRows && pageRows.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          {pageRows.map((s, i) => (
            s.columns ? <RowView key={s.id || i} row={s} data={{}} /> : null
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <input value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && search()}
          placeholder="دنبال چه مطلبی می‌گردی؟" style={{ flex: 1, minWidth: 0, padding: '13px 18px', borderRadius: 'var(--shape-btn)', border: '1.5px solid var(--border)', fontFamily: 'inherit', fontSize: '0.92rem', background: 'var(--card)', color: 'var(--fg)', outline: 'none', transition: 'border-color 0.2s' }} />
        <button onClick={() => search()} style={{ padding: '13px 30px', borderRadius: 'var(--shape-btn)', border: 'none', background: 'var(--primary)', color: 'var(--on-primary)', fontFamily: 'inherit', fontWeight: 800, cursor: 'pointer', flexShrink: 0 }}>جستجو</button>
      </div>
      {cats.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
          <button onClick={() => { setActiveCat(''); search(''); }}
            style={{ padding: '7px 18px', borderRadius: 999, border: '1.5px solid', borderColor: !activeCat ? 'var(--primary)' : 'var(--border)', background: !activeCat ? 'var(--primary)' : 'var(--card)', color: !activeCat ? 'var(--on-primary)' : 'var(--muted-fg)', fontFamily: 'inherit', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer' }}>همه</button>
          {cats.map(c => (
            <button key={c.id} onClick={() => { setActiveCat(c.slug); search(c.slug); }}
              style={{ padding: '7px 18px', borderRadius: 999, border: '1.5px solid', borderColor: activeCat === c.slug ? 'var(--primary)' : 'var(--border)', background: activeCat === c.slug ? 'var(--primary)' : 'var(--card)', color: activeCat === c.slug ? 'var(--on-primary)' : 'var(--muted-fg)', fontFamily: 'inherit', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer' }}>
              {c.name}{Number(c.posts_count || 0) > 0 ? ` (${Number(c.posts_count).toLocaleString('fa-IR')})` : ''}
            </button>
          ))}
        </div>
      )}
      {loading ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
          {[0, 1, 2, 3, 4, 5].map(i => (
            <div key={i}><Skeleton height={170} style={{ borderRadius: 14 }} /><Skeleton height={18} width="80%" style={{ marginTop: 10 }} /><Skeleton height={14} width="50%" style={{ marginTop: 8 }} /></div>
          ))}
        </div>
      ) : posts.length ? (
        <>
          {hero && (() => {
            const src = hero.cover ? coverSrc(hero.cover) : null;
            return (
              <Link to={`/blog/${hero.slug || hero.id}`} style={{ textDecoration: 'none', color: 'inherit', display: 'block', marginBottom: 20 }}>
                <div className="pcard-shell" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 0, border: '1px solid var(--border)', borderRadius: 'var(--shape-card)', overflow: 'hidden', background: 'var(--card)' }}>
                  {src && <img src={src} alt={hero.title} style={{ width: '100%', aspectRatio: '21/9', objectFit: 'cover', display: 'block', background: 'var(--muted)' }} />}
                  <div style={{ padding: '18px 20px' }}>
                    <span style={{ fontSize: '0.72rem', fontWeight: 800, color: 'var(--primary)', background: 'color-mix(in srgb, var(--primary) 14%, transparent)', borderRadius: 999, padding: '5px 14px' }}>منتخب سردبیر</span>
                    <div className="serif" style={{ fontWeight: 700, fontSize: 'clamp(1.1rem, 3vw, 1.5rem)', lineHeight: 1.8, marginTop: 8 }}>{hero.title}</div>
                    {hero.excerpt && <div style={{ fontSize: '0.88rem', color: 'var(--muted-fg)', lineHeight: 2, marginTop: 6 }}>{String(hero.excerpt).slice(0, 160)}{String(hero.excerpt).length > 160 ? '…' : ''}</div>}
                    <div style={{ fontSize: '0.75rem', color: 'var(--muted-fg)', marginTop: 8 }}>
                      {hero.author || 'تحریریه'}
                      {hero.published_at ? ` · ${new Date(hero.published_at).toLocaleDateString('fa-IR')}` : ''}
                    </div>
                  </div>
                </div>
              </Link>
            );
          })()}
          {rest.length > 0 ? (
            <DefaultTemplateList kind="post" items={rest} fallbackTpl={{ layout: 'grid' }} />
          ) : (
            <DefaultTemplateList kind="post" items={posts} fallbackTpl={{ layout: 'grid' }} />
          )}
        </>
      ) : (
        <div style={{ textAlign: 'center', color: 'var(--muted-fg)', padding: '60px 20px', border: '2px dashed var(--border)', borderRadius: 'var(--shape-card)' }}>
          <div style={{ fontWeight: 800, color: 'var(--fg)', marginBottom: 8 }}>هنوز مطلبی منتشر نشده</div>
          <div style={{ fontSize: '0.86rem' }}>به‌زودی آموزش‌ها و راهنماهای خودرویی اینجا قرار می‌گیرد.</div>
        </div>
      )}
      </div>
      <aside className="blog-side">
        {cats.length > 0 && (
          <div className="side-card">
            <div className="side-title">دسته‌بندی‌ها</div>
            <div style={{ display: 'grid', gap: 2 }}>
              <button className="side-link" onClick={() => { setActiveCat(''); search(''); }} style={{ border: 'none', background: !activeCat ? 'color-mix(in srgb, var(--primary) 10%, transparent)' : 'transparent', cursor: 'pointer', fontFamily: 'inherit', width: '100%' }}>
                <span>همه مطالب</span><span style={{ color: 'var(--muted-fg)', fontSize: '0.76rem' }}>{Number(total || 0).toLocaleString('fa-IR')}</span>
              </button>
              {cats.map(c => (
                <button key={c.id} className="side-link" onClick={() => { setActiveCat(c.slug); search(c.slug); }} style={{ border: 'none', background: activeCat === c.slug ? 'color-mix(in srgb, var(--primary) 10%, transparent)' : 'transparent', cursor: 'pointer', fontFamily: 'inherit', width: '100%' }}>
                  <span>{c.name}</span><span style={{ color: 'var(--muted-fg)', fontSize: '0.76rem' }}>{Number(c.posts_count || 0).toLocaleString('fa-IR')}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        {posts.length > 0 && (
          <div className="side-card">
            <div className="side-title">پربازدیدترین‌ها</div>
            <div style={{ display: 'grid', gap: 4 }}>
              {posts.slice(0, 4).map((r, i) => (
                <Link key={'v' + r.id} to={`/blog/${r.slug || r.id}`} className="side-link">
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, lineHeight: 1.8 }}>
                    <span style={{ width: 22, height: 22, borderRadius: 999, background: 'color-mix(in srgb, var(--primary) 14%, transparent)', color: 'var(--primary)', fontSize: '0.72rem', fontWeight: 900, display: 'inline-grid', placeItems: 'center', flexShrink: 0 }}>{i + 1}</span>
                    {String(r.title).slice(0, 40)}{String(r.title).length > 40 ? '…' : ''}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}
        {posts.length > 0 && (
          <div className="side-card">
            <div className="side-title">تازه‌ترین مطالب</div>
            <div style={{ display: 'grid', gap: 4 }}>
              {posts.slice(0, 5).map(r => (
                <Link key={r.id} to={`/blog/${r.slug || r.id}`} className="side-link">
                  <span style={{ lineHeight: 1.8 }}>{String(r.title).slice(0, 46)}{String(r.title).length > 46 ? '…' : ''}</span>
                  {r.published_at && <span style={{ color: 'var(--muted-fg)', fontSize: '0.72rem', whiteSpace: 'nowrap' }}>{new Date(r.published_at).toLocaleDateString('fa-IR')}</span>}
                </Link>
              ))}
            </div>
          </div>
        )}
        {topics.length > 0 && (
        <div className="side-card">
          <div className="side-title">موضوعات</div>
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
            {topics.map(t => (
              <button key={t} onClick={() => { setQuery(t); search('', t); }}
                style={{ padding: '5px 11px', borderRadius: 999, border: '1px solid var(--border)', background: 'transparent', color: 'var(--fg)', fontFamily: 'inherit', fontSize: '0.74rem', fontWeight: 700, cursor: 'pointer' }}>#{t}</button>
            ))}
          </div>
        </div>
        )}
        <div className="side-card" style={{ background: 'linear-gradient(150deg, color-mix(in srgb, var(--primary) 16%, var(--card)), var(--card) 75%)' }}>
          <div className="side-title">خبرنامه {siteName || 'ما'}</div>
          <div style={{ fontSize: '0.82rem', color: 'var(--muted-fg)', lineHeight: 2, marginBottom: 10 }}>آموزش‌ها و نکات تازه را در ایمیلت بگیر.</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <input placeholder="ایمیل شما" style={{ flex: 1, minWidth: 0, padding: '10px 12px', borderRadius: 'var(--shape-btn)', border: '1px solid var(--border)', background: 'var(--card)', color: 'var(--fg)', fontFamily: 'inherit', fontSize: '0.82rem', outline: 'none' }} />
            <button style={{ padding: '10px 16px', borderRadius: 'var(--shape-btn)', border: 'none', background: 'var(--primary)', color: 'var(--on-primary)', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.82rem' }}>عضویت</button>
          </div>
        </div>
      </aside>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginTop: 30 }}>
        {[['آموزش تخصصی', 'گام‌به‌گام و تصویری، مخصوص همین حوزه'],
          ['به‌روز و کاربردی', 'جدیدترین نکات و راهنماها، بدون حاشیه'],
          ['رایگان و همیشگی', 'دسترسی آزاد به همه آموزش‌ها و راهنماها']].map(([t, d]) => (
          <div key={t} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--shape-card)', padding: '16px 18px' }}>
            <span style={{ width: 38, height: 38, borderRadius: 12, background: 'color-mix(in srgb, var(--primary) 13%, transparent)', color: 'var(--primary)', display: 'inline-grid', placeItems: 'center', fontWeight: 900, flexShrink: 0 }}>✓</span>
            <div>
              <div style={{ fontWeight: 800, fontSize: '0.9rem', marginBottom: 4 }}>{t}</div>
              <div style={{ fontSize: '0.79rem', color: 'var(--muted-fg)', lineHeight: 2 }}>{d}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
